import { useRef, useState } from "react";
import { useAuth } from "../../../../firebase/AuthContext";
import {
  confirmImport,
  getTemplateUrl,
  uploadForPreview,
} from "../api/excelImport";

function ImportTab() {
  const { currentUser } = useAuth();
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);

  const processFile = async (file) => {
    setError("");
    setResult(null);
    setSelectedFile(file);
    setUploading(true);
    try {
      const token = await currentUser.getIdToken();
      setPreview(await uploadForPreview(token, file));
    } catch (err) {
      console.error("[ImportTab] Failed to upload file:", err);
      setError("Failed to upload the file. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) processFile(file);
  };

  const clearFile = () => {
    setSelectedFile(null);
    setPreview(null);
    setResult(null);
    setError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleConfirm = async () => {
    if (!preview || !preview.valid) return;
    setSubmitting(true);
    try {
      const token = await currentUser.getIdToken();
      setResult(await confirmImport(token, preview.rows));
      setPreview(null);
    } catch (err) {
      console.error("[ImportTab] Import failed:", err);
      setError("Import failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-8 relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-40 h-40 bg-orange-500/5 rounded-full -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-orange-500/5 rounded-full translate-y-1/2 -translate-x-1/2" />

        {/* Header */}
        <div className="relative mb-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-500/10 rounded-2xl mb-4">
            <svg
              className="w-8 h-8 text-orange-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
              />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            Bulk Import FAQs
          </h2>
          <p className="text-gray-600 dark:text-gray-400 max-w-lg mx-auto">
            Upload an Excel file (.xlsx) to bulk-import FAQs. Each row is
            validated against the required column format (language, category,
            question, answer, source URL, keywords, image URLs) — sheet names
            and categories are free-form, but if any row fails validation, the
            whole file is rejected and nothing gets imported.
          </p>
          <a
            href={getTemplateUrl()}
            className="inline-flex items-center gap-2 mt-4 px-4 py-2 text-sm font-medium text-white bg-orange-500 hover:bg-orange-600 rounded-lg shadow-sm transition-colors"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
              />
            </svg>
            Download template
          </a>
        </div>

        <div className="relative space-y-6">
          {/* Drop Zone */}
          <div
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`relative border-2 border-dashed rounded-2xl p-10 text-center transition-all duration-300 cursor-pointer ${
              isDragging || selectedFile
                ? "border-orange-500/50 bg-orange-500/5"
                : "border-gray-300 dark:border-gray-600 hover:border-orange-500/50 hover:bg-gray-50 dark:hover:bg-gray-700/40"
            }`}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx"
              onChange={handleFileChange}
              className="hidden"
            />

            <div
              className={`mb-4 inline-flex items-center justify-center w-16 h-16 rounded-2xl transition-all duration-300 ${
                selectedFile
                  ? "bg-orange-500/20"
                  : "bg-gray-100 dark:bg-gray-700"
              }`}
            >
              {selectedFile ? (
                <svg
                  className="w-8 h-8 text-orange-500"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
              ) : (
                <svg
                  className="w-8 h-8 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                  />
                </svg>
              )}
            </div>

            <div className="text-gray-700 dark:text-gray-300 mb-1">
              {uploading ? (
                "Uploading..."
              ) : selectedFile ? (
                <span className="text-orange-500 font-medium">
                  File selected
                </span>
              ) : (
                <>
                  <span className="text-orange-500 font-medium">
                    Click to upload
                  </span>{" "}
                  or drag and drop
                </>
              )}
            </div>

            <p className="text-gray-500 dark:text-gray-400 text-sm">
              Only .xlsx files are accepted
            </p>
          </div>

          {/* Selected File Info */}
          {selectedFile && (
            <div className="bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 rounded-xl p-4 animate-fadeIn">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-4 min-w-0">
                  <div className="w-12 h-12 bg-green-500/10 rounded-xl flex items-center justify-center shrink-0">
                    <svg
                      className="w-6 h-6 text-green-600"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="min-w-0">
                    <p className="text-gray-900 dark:text-gray-100 font-medium truncate">
                      {selectedFile.name}
                    </p>
                    <p className="text-gray-600 dark:text-gray-400 text-sm">
                      {(selectedFile.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    clearFile();
                  }}
                  className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all shrink-0"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">
          {error}
        </div>
      )}

      {preview && (
        <div className="flex flex-col gap-3">
          {preview.valid ? (
            <>
              <p className="text-sm text-slate-700 dark:text-gray-300">
                {preview.rows.length} row
                {preview.rows.length === 1 ? "" : "s"} validated successfully —
                review them below, then confirm to import.
              </p>
              <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-gray-700">
                <table className="w-full text-sm text-left">
                  <thead className="text-orange-700 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/20 border-b border-orange-200 dark:border-orange-900/40">
                    <tr>
                      <th className="py-2 px-4">Sheet</th>
                      <th className="py-2 px-4">Row</th>
                      <th className="py-2 px-4">Language</th>
                      <th className="py-2 px-4">Category</th>
                      <th className="py-2 px-4">Question</th>
                      <th className="py-2 px-4">Answer</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.rows.map((row, i) => (
                      <tr
                        key={i}
                        className="border-b border-slate-100 dark:border-gray-700/50"
                      >
                        <td className="py-2 px-4 text-slate-700 dark:text-gray-300">
                          {row.sheet}
                        </td>
                        <td className="py-2 px-4 text-slate-500 dark:text-gray-400">
                          {row.row_number}
                        </td>
                        <td className="py-2 px-4 text-slate-500 dark:text-gray-400">
                          {row.language}
                        </td>
                        <td className="py-2 px-4 text-slate-500 dark:text-gray-400">
                          {row.category}
                        </td>
                        <td className="py-2 px-4 text-slate-900 dark:text-gray-100 max-w-xs truncate">
                          {row.question}
                        </td>
                        <td className="py-2 px-4 text-slate-500 dark:text-gray-400 max-w-xs truncate">
                          {row.answer}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <button
                onClick={handleConfirm}
                disabled={submitting}
                className="self-start px-4 py-2 text-sm font-medium text-white bg-orange-500 hover:bg-orange-600 rounded-lg disabled:opacity-50 transition-colors"
              >
                {submitting ? "Importing..." : "Confirm Import"}
              </button>
            </>
          ) : (
            <>
              <p className="text-red-600 dark:text-red-400 text-sm">
                Validation failed — {preview.errors.length} error(s) found:
              </p>
              <ul className="text-sm text-red-700 dark:text-red-400 list-disc pl-5">
                {preview.errors.map((err, i) => (
                  <li key={i}>
                    [{err.sheet}] row {err.row_number}, {err.field}:{" "}
                    {err.reason}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}

      {result && (
        <div className="bg-orange-50/40 dark:bg-orange-900/10 border border-orange-200 dark:border-orange-900/40 rounded-xl p-4">
          <p className="text-sm text-slate-900 dark:text-gray-100">
            Successfully imported {result.imported_count} row(s).
          </p>
          {result.failed.length > 0 && (
            <ul className="text-sm text-red-700 dark:text-red-400 list-disc pl-5 mt-2">
              {result.failed.map((err, i) => (
                <li key={i}>
                  [{err.sheet}] row {err.row_number}: {err.reason}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export default ImportTab;
